import type { Job, JobWorker, RealtimeEvent, WorkerJobStatus, WorkerResponse, WorkerStatus } from '../types'
import type { RealtimeConnection, SocketHandlers } from './data-source'

const WORKER_STATUSES: WorkerStatus[] = ['available', 'on_job', 'late', 'offline']
const WORKER_RESPONSES: WorkerResponse[] = ['pending', 'accepted', 'declined', 'expired']
const WORKER_JOB_STATUSES: WorkerJobStatus[] = ['assigned', 'checked_in', 'on_the_way', 'started', 'completed']

/** Exponential backoff with "equal jitter": half fixed, half random, capped at `maxMs`. */
export function computeBackoffDelay(
  attempt: number,
  { baseMs = 1_000, maxMs = 30_000, random = Math.random }: { baseMs?: number; maxMs?: number; random?: () => number } = {},
): number {
  const exponential = Math.min(maxMs, baseMs * 2 ** Math.max(0, attempt))
  return Math.round(exponential / 2 + random() * (exponential / 2))
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

/** Validates an untrusted socket payload into a RealtimeEvent; returns null when malformed. */
export function parseRealtimeEvent(raw: unknown): RealtimeEvent | null {
  if (!isRecord(raw) || typeof raw.type !== 'string') return null
  const at = typeof raw.at === 'string' ? raw.at : new Date().toISOString()

  const jobId = typeof raw.jobId === 'string' ? raw.jobId : null
  const workerId = typeof raw.workerId === 'string' ? raw.workerId : null

  switch (raw.type) {
    case 'worker.location.updated':
    case 'worker.location':
      return workerId && isFiniteNumber(raw.lat) && isFiniteNumber(raw.lng)
        ? { type: raw.type, workerId, lat: raw.lat, lng: raw.lng, at }
        : null
    case 'job.deleted':
      return jobId ? { type: raw.type, jobId } : null
    case 'job.worker.added': {
      const worker = isRecord(raw.worker) ? raw.worker : null
      if (!jobId || !worker || typeof worker.workerId !== 'string') return null
      return {
        type: raw.type,
        jobId,
        worker: {
          response: 'pending',
          status: 'assigned',
          sentAt: at,
          ...(worker as unknown as Partial<JobWorker>),
          workerId: worker.workerId,
        } as JobWorker,
      }
    }
    case 'job.worker.removed':
      return jobId && workerId ? { type: raw.type, jobId, workerId, reason: typeof raw.reason === 'string' ? raw.reason : null } : null
    case 'job.worker.response':
      return jobId && workerId && WORKER_RESPONSES.includes(raw.response as WorkerResponse)
        ? { type: raw.type, jobId, workerId, response: raw.response as WorkerResponse, at, declineReason: typeof raw.declineReason === 'string' ? raw.declineReason : undefined }
        : null
    case 'job.worker.status':
      return jobId && workerId && WORKER_JOB_STATUSES.includes(raw.status as WorkerJobStatus)
        ? { type: raw.type, jobId, workerId, status: raw.status as WorkerJobStatus, at }
        : null
    case 'job.worker.reminded':
      return jobId && workerId ? { type: raw.type, jobId, workerId, at } : null
    case 'job.required.changed':
      return jobId && isFiniteNumber(raw.requiredWorkers) ? { type: raw.type, jobId, requiredWorkers: raw.requiredWorkers } : null
    case 'worker.status.changed':
    case 'worker.status':
      return typeof raw.workerId === 'string' && WORKER_STATUSES.includes(raw.status as WorkerStatus)
        ? {
            type: raw.type,
            workerId: raw.workerId,
            status: raw.status as WorkerStatus,
            currentJobId: typeof raw.currentJobId === 'string' || raw.currentJobId === null ? raw.currentJobId : undefined,
            at,
          }
        : null
    case 'job.created':
      return isRecord(raw.job) && typeof raw.job.id === 'string' ? { type: raw.type, job: raw.job as unknown as Job } : null
    case 'job.updated':
      return isRecord(raw.job) && typeof raw.job.id === 'string'
        ? { type: raw.type, job: raw.job as Partial<Job> & { id: string } }
        : null
    case 'job.assigned':
      return typeof raw.jobId === 'string' && typeof raw.workerId === 'string'
        ? { type: raw.type, jobId: raw.jobId, workerId: raw.workerId, at }
        : null
    default:
      return null
  }
}

/** Accepts a single event, an array of events, or `{ events: [...] }`. */
export function parseSocketMessage(data: unknown): RealtimeEvent[] {
  let payload: unknown = data
  if (typeof data === 'string') {
    try {
      payload = JSON.parse(data)
    } catch {
      return []
    }
  }
  const items = Array.isArray(payload)
    ? payload
    : isRecord(payload) && Array.isArray(payload.events)
      ? payload.events
      : [payload]
  return items.map(parseRealtimeEvent).filter((event): event is RealtimeEvent => event != null)
}

/**
 * WebSocket with automatic reconnect (exponential backoff + jitter). Reconnects immediately
 * when the browser comes back online.
 */
export function createReconnectingSocket(
  url: string,
  handlers: SocketHandlers,
  { maxAttempts = Number.POSITIVE_INFINITY }: { maxAttempts?: number } = {},
): RealtimeConnection {
  let socket: WebSocket | null = null
  let attempt = 0
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let closedByClient = false

  const clearRetry = () => {
    if (retryTimer) clearTimeout(retryTimer)
    retryTimer = null
  }

  const open = () => {
    clearRetry()
    if (closedByClient) return
    try {
      socket = new WebSocket(url)
    } catch {
      scheduleReconnect()
      return
    }

    socket.onopen = () => {
      attempt = 0
      handlers.onOpen()
    }
    socket.onmessage = (message) => {
      parseSocketMessage(message.data).forEach(handlers.onEvent)
    }
    socket.onclose = () => {
      socket = null
      if (!closedByClient) scheduleReconnect()
    }
    // onclose always follows onerror; reconnect is scheduled there.
    socket.onerror = () => undefined
  }

  const scheduleReconnect = () => {
    const willRetry = attempt < maxAttempts
    const delayMs = willRetry ? computeBackoffDelay(attempt) : 0
    handlers.onClose({ willRetry, attempt, delayMs })
    if (!willRetry) return
    attempt += 1
    retryTimer = setTimeout(open, delayMs)
  }

  const handleOnline = () => {
    if (!socket) {
      attempt = 0
      open()
    }
  }

  window.addEventListener('online', handleOnline)
  open()

  return {
    close: () => {
      closedByClient = true
      clearRetry()
      window.removeEventListener('online', handleOnline)
      if (socket) {
        socket.onclose = null
        socket.close()
        socket = null
      }
    },
    reconnectNow: () => {
      if (closedByClient) return
      attempt = 0
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return
      open()
    },
  }
}
